import { Component, OnInit, Renderer, Input, Output, EventEmitter, ElementRef,
  HostBinding, forwardRef, ContentChildren, ViewChild, ViewChildren, QueryList } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NG_VALIDATORS, FormControl } from '@angular/forms';
import { CfCoreComponent } from '../core/core.component';
import { IconModel } from '../../models/icon/icon.model';
import { SelectModel } from '../../models/select/select.model';
import { SelectOptionModel } from '../../models/select/select-option.model';
import { SelectStylingModel } from '../../models/select/select-styling.model';
import { SelectTemplates } from '../../templates/select.template';
import { CfSelectOptionComponent } from '../select-option/select-option.component';
import { TemplateService } from '../../services/template-service/template.service';
/**
 * <p>CF Select Component</p>
 * <pre>
 * <code>
 * import {{'{'}} SelectModel {{'}'}} from 'cedrus-fusion'
 * import {{'{'}} SelectStylingModel {{'}'}} from 'cedrus-fusion'
 * <code><</code>cf-select<code>></code><code><</code><code>/</code>cf-select<code>></code>
 * </code>
 * </pre>
 */
export class CfSelectComponent extends CfCoreComponent implements OnInit {
/**
 * @hidden 
 * Bind class to component :host itself
 */
notificationClickable = '';
/**
 * @hidden 
 * List of children options from html view
 */
contentOptions: QueryList<CfSelectOptionComponent>;
/**
 * <b>SelectModel</b>
 * <p>Example of <b>SelectModel</b> Class:</p>
 * <pre>{{'{'}}
 *  placeholder: 'Select fruit: ',
 *  staticPlaceholder: 'Open list',
 *  optionsSource: 'fromModel',
 *  options: [
 *      {{'{'}} val: 'apl', text: 'Apple' {{'}'}},
 *      {{'{'}} val: 'ban', text: 'Banana' {{'}'}},
 *      {{'{'}} val: 'che', text: 'Cherry' {{'}'}}
 * ],
 *  selected: 'che',
 *  showFilter: false,
 *  showIcon: false,
 *  iconProperty: this.myFruitsIcon,
 *  iconChangeable: false,
 *  required: false,
 *  multiple: true,
 *  dropdownUnder: true,
 *  optionValueField: 'val',
 *  optionLabelField: 'text',
 *  optionItemField: null,
 *  optionIconField: null
 * {{'}'}}</pre>
 * <h4>placeholder: string</h4>
 * <p>Placeholder text</p>
 * <h4>staticPlaceholder: string</h4>
 * <p>Not changable static placeholder text</p>
 * <h4>optionsSource: string</h4>
 * <p>Options source type</p>
 * <h4>options: any[]</h4>
 * <p>Array with options</p>
 * <h4>selected: any</h4>
 * <p>Select component must have some selected item and it is value of item property described in <b>itemValue</b></p>
 * <h4>showFilter: boolean</h4>
 * <p>Show/Hide filter</p>
 * <h4>showIcon: boolean</h4>
 * <p>Show/Hide icon</p>
 * <h4>iconProperty: IconModel</h4>
 * <p>Icon</p>
 * <h4>iconChangeable: boolean</h4>
 * <p>If main icon can be changed with selected option icon</p>
 * <h4>required: boolean</h4>
 * <p>Required</p>
 * <h4>multiple: boolean</h4>
 * <p>Options multipleselection</p>
 * <h4>dropdownUnder: boolean</h4>
 * <p>If select dropdown must be under it placeholder</p>
 * <h4>optionValueField: string</h4>
 * <p>Option property name to be used as option value</p>
 * <h4>optionLabelField: string</h4>
 * <p>Option property name to be used as select text when option is selected and as option text if item property is not specified</p>
 * <h4>optionItemField: string</h4>
 * <p>Option property name to be used as option item</p>
 * <h4>optionIconField: string</h4>
 * <p>Option property name to be used as option icon</p>
 */
public properties: SelectModel;
/**
 * <pre>{{'{'}}
 * // styling of the container surrounding the select
 * container: {{'{'}}
 * 		dynamic class: function() -> string, //function that returns a string of the name of the class
 * 		class: string //name of the class specified in you scss/css file
 * 	{{'}'}},
 * // Styling of select trigger by StylingModel
 * select: {{'{'}}
 * 		dynamic class: function() -> string,
 * 		class: string ,
 * 		themeColor: string , // primary/accent/warn
 * {{'}'}},
 * // Styling of filter by StylingModel
 * filter: {{'{'}}
 * 		dynamic class: function() -> string,
 * 		class: string
 * {{'}'}},
 * // Styling of options by StylingModel
 * options: {{'{'}}
 *    dynamic class: function() -> string,
 *    class: string
 * {{'}'}},
 * // Styling of static placeholder by StylingModel
 * staticPlaceholder: {{'{'}}
 *    dynamic class: function() -> string,
 *    class: string
 * {{'}'}},
 * // Styling of dropdown panel by StylingModel
 * dropdownPanel: {{'{'}}
 * 		dynamic class: function() -> string,
 * 		class: string
 * {{'}'}}
 * {{'}'}}
 * </pre>
 */
public styling: SelectStylingModel;
/**
 * <p>Text for placeholder</p>
 * @param {?} value
 * @return {?}
 */
set placeholder(value: string) { this.properties.placeholder = value; }
/**
 * <p>Not changable static placeholder text</p>
 * @param {?} value
 * @return {?}
 */
set staticPlaceholder(value: string) { this.properties.staticPlaceholder = value; }
/**
 * <p>Source of options. For current time can be only <b>fromModel</b>
 * and means that options will be set by SelectModel call. If that property
 * is not specified it means that options will be created from the view.</p>
 * @param {?} value
 * @return {?}
 */
set optionsSource(value: string) { this.properties.optionsSource = value; }
/**
 * <p>Array with options to be send to SelectModel</p>
 * @param {?} value
 * @return {?}
 */
set options(value: SelectOptionModel[]) { this.properties.options = value; }
/**
 * <p>Value to select some option</p>
 * @param {?} value
 * @return {?}
 */
set selected(value: any) { this.properties.selected = value; }
/**
 * <p>If to show filter in dropdown list</p>
 * @param {?} value
 * @return {?}
 */
set showFilter(value: boolean) { this.properties.showFilter = value; }
/**
 * <p>If to show select icon</p>
 * @param {?} value
 * @return {?}
 */
set showIcon(value: boolean) { this.properties.showIcon = value; }
/**
 * <p>IconModel object</p>
 * @param {?} value
 * @return {?}
 */
set iconProperty(value: IconModel) { this.properties.iconProperty = value; }
/**
 * <p>If main icon can be changed with selected option icon</p>
 * @param {?} value
 * @return {?}
 */
set iconChangeable(value: boolean) { this.properties.iconChangeable = value; }
/**
 * <p>If select is required</p>
 * @param {?} value
 * @return {?}
 */
set required(value: boolean) { this.properties.required = value; }
/**
 * <p>If select has multipleselection options</p>
 * @param {?} value
 * @return {?}
 */
set multiple(value: boolean) { this.properties.multiple = value; }
/**
 * <p>If select dropdown must be under it placeholder</p>
 * @param {?} value
 * @return {?}
 */
set dropdownUnder(value: boolean) { this.properties.dropdownUnder = value; }
/**
 * <p>Option property name to be used as option value</p>
 * @param {?} value
 * @return {?}
 */
set optionValueField(value: string) { this.properties.optionValueField = value; }
/**
 * <p>Option property name to be used as select text when option is selected and as option text if item property is not specified</p>
 * @param {?} value
 * @return {?}
 */
set optionLabelField(value: string) { this.properties.optionLabelField = value; }
/**
 * <p>Option property name to be used as option item</p>
 * @param {?} value
 * @return {?}
 */
set optionItemField(value: string) { this.properties.optionItemField = value; }
/**
 * <p>Option property name to be used as option icon</p>
 * @param {?} value
 * @return {?}
 */
set optionIconField(value: string) { this.properties.optionIconField = value; }
/**
 * <p>Event to call when Select dropdown list was opened</p>
 */
onOpen = new EventEmitter<any>();
/**
 * <p>Event to call when value of Select was changed</p>
 */
onChange = new EventEmitter<any>();
/**
 * <p>Event to call when Select dropdown list was closed</p>
 */
onClose = new EventEmitter<any>();
/**
 * @hidden
 */
selectTrigger;
/**
 * @hidden
 */
optionsTmpls;
/**
 * @hidden 
 * <p>Value of the Filter input by which whole dropdown list items will be filtered<p>
 */
cfFilterBy = '';
/**
 * @hidden 
 * True or false whether md-select is focused
 */
selectIsFocused: boolean;
/**
 * @hidden
 */
_lastOffsetLeft;
/**
 * @hidden 
 * It is method which will emit corresponding event when Select dropdown list was opened.
 * @return {?}
 */
selectOpened() {
    this.onOpen.emit();
    this.makeDropdownUnder();
  }
/**
 * @hidden
 * @param {?} obj
 * @return {?}
 */
writeValue(obj: any): void {
    if (obj) {
      if (this.properties != null) { this.properties['selected'] = obj; }
    }
  }
/**
 * @hidden
 * @param {?} fn
 * @return {?}
 */
registerOnChange(fn: any): void {
    this.onValueChange = (value) => { Promise.resolve(null).then(() => fn(value)) };
  }
/**
 * @hidden
 * @param {?} fn
 * @return {?}
 */
registerOnTouched(fn: any): void {
    this.onTouched = (value) => { Promise.resolve(null).then(() => fn(value)) };
  }
/**
 * @hidden
 * @param {?} isDisabled
 * @return {?}
 */
setDisabledState(isDisabled: boolean): void { }
/**
 * @hidden
 */
onValueChange: any = () => { };
/**
 * @hidden
 */
onTouched: any = () => { };
/**
 * hidden
 * It is method which will emit corresponding event when value of Select was changed.
 * @return {?}
 */
selectChanged() {
    this.onChange.emit(this.properties['selected']);
    this.onValueChange(this.properties['selected']);
    this.onTouched();
    if (this.properties.iconProperty && this.properties.iconProperty.name !== '' && this.properties.iconChangeable) { this.reflectIcon(); }
  }
/**
 * hidden
 * It is method which will emit corresponding event when Select dropdown list was closed.
 * @return {?}
 */
selectClosed() { this.onClose.emit(); }
/**
 * hidden
 * It is function for creating Notification click events by using Core Component <b>notificationAction</b> event emmiter.
 * @param {?} notification    it is json notification object
 * @return {?}
 */
cfNotificationAction(notification) { this.notificationAction.emit(notification); }
/**
 * @hidden
 * @return {?}
 */
resetSelect() {
    this.properties.selected = '';
    this.selectChanged();
  }
/**
 * @hidden
 * @param {?} _renderer
 * @param {?} elementRef
 * @param {?} templateService
 */
constructor(private _renderer: Renderer,
public elementRef: ElementRef, /**@hidden */ templateService: TemplateService ) {
    super(elementRef, templateService);
    this.properties = new SelectModel();
    this.styling = new SelectStylingModel();
  }
/**
 * @hidden 
 * It will be generated <b>cfSelect</b> object and rendered inside component template.
 * @return {?}
 */
ngOnInit() {
    if (this.notificationAction.observers.length > 0) { this.notificationClickable = 'notification-clickable'; }

    this.getMyTemplate('select', SelectTemplates).then(() => {
      this.mergeProperties(this.activeTemplate.property, this.properties, 'SELECT').then(sampler => {
        setTimeout(() => { this.reflectIcon(); }, 0);
        if (this.properties.optionsSource !== 'fromModel') { this.optionsTmpls.changes.subscribe(val => this._checkTemplates()); }
        this.checkUnderline();
        // console.log('SELECT', this);
      });
    });
  }
/**
 * @hidden 
 * Function for filter dropdown options list
 * @param {?} searchVal  string text value by which dropdown options list will be filtered
 * @return {?}
 */
filterOptions(searchVal: any) {
    const /** @type {?} */ source = this.properties.optionsSource === 'fromModel' ? 'model' : 'content';
    let /** @type {?} */ filtered;
    if (source === 'model') {
      filtered = this.properties.options.filter(function(d) {
        return JSON.stringify(d).toLowerCase().indexOf(searchVal) !== -1 || !searchVal;
      });
    } else {
      filtered = this.contentOptions.filter(function(d) {
        return JSON.stringify(d.properties).toLowerCase().indexOf(searchVal) !== -1 || !searchVal;
      });
    }
    return searchVal ? filtered : (source === 'model' ? this.properties.options : this.contentOptions);
  }
/**
 * @hidden 
 * Function to reflect selected option icon into select icon
 * @return {?}
 */
reflectIcon() {
    const /** @type {?} */ options = this.properties && this.properties.optionsSource === 'fromModel' ?
                    this.properties.options : this.contentOptions.toArray();

    if (this.properties.selected && options && this.properties.iconChangeable) {
      if (this.properties.optionsSource === 'fromModel') {
        let /** @type {?} */ iconField = this.properties.optionIconField;
        options.forEach(option => {
          if (option[this.properties.optionValueField] === this.properties.selected && option.hasOwnProperty(iconField)) {
            this.properties.iconProperty.name = option[iconField].name;
            this.properties.iconProperty.type = option[iconField].name.startsWith('fa-') ? 'fa' : 'mi';
          }
        });
      } else {
        options.forEach(option => {
          if (option.properties.value === this.properties.selected && option.properties.hasOwnProperty('icon')) {
            this.properties.iconProperty.name = option.properties.icon.name;
            this.properties.iconProperty.type = option.properties.icon.name.startsWith('fa-') ? 'fa' : 'mi';
          }
        });
      }
    }
  }
/**
 * @hidden 
 * Method to check each single option item if it has template (html content)
 * @return {?}
 */
_checkTemplates() {
    setTimeout( () => {
      const /** @type {?} */ templates = this.optionsTmpls.toArray();
      this.contentOptions.forEach((option, i) => {
        if (!option._templateInited && templates[i]) {
          option._hasTemplate = templates[i].nativeElement.children.length > 0;
          option._templateInited = true;
        }
      });
    }, 0);
  }
/**
 * @hidden 
 * Function to check if md-select element is focused and adding class 'cf-select-is-focused' to main component div
 * @param {?} is  boolead true or false whether md-select is focused
 * @return {?}
 */
setFocused(is) { this.selectIsFocused = is; }
/**
 * @hidden 
 * Function to move dropdown panel under the select trigger
 * @return {?}
 */
makeDropdownUnder() {
    if (this.properties.dropdownUnder) {
      let /** @type {?} */ triggerTop = this.nativeElement.getBoundingClientRect().top, /** @type {?} */
        panel = document.getElementsByClassName(this.id)[0], /** @type {?} */
        panelTop = panel.getBoundingClientRect().top, /** @type {?} */
        panelDiff = (triggerTop - panelTop + this.nativeElement.offsetHeight - 3) + 'px';

      this._renderer.setElementStyle(panel, 'marginTop', panelDiff);
    }
  }
/**
 * @hidden
 * @return {?}
 */
checkUnderline() {
    setTimeout(() => {
      let /** @type {?} */ select = this.nativeElement.getElementsByClassName('mat-select')[0];
      if (select && select.offsetLeft > 0 && select.offsetLeft !== this._lastOffsetLeft) {
        let /** @type {?} */ line = this.nativeElement.getElementsByClassName('mat-select-underline');
        this._renderer.setElementStyle(line[0], 'marginLeft', '-' + select.offsetLeft + 'px');
        this._lastOffsetLeft = select.offsetLeft;
      }
    }, 0);
  }
/**
 * @hidden 
 * Method to set 'mat-selected' class to the option.cf-option-data when option is selected
 * @param {?} option  option object
 * @return {?}
 */
checkIfSelected(option) {
    let /** @type {?} */ isSelected = false;
    if (this.properties.multiple) {
      if (Array.isArray(this.properties.selected)) {
        if (this.properties.optionsSource === 'fromModel') {
          if (this.properties.selected.indexOf(option[this.properties.optionValueField]) !== -1) { isSelected = true; }
        } else {
          if (this.properties.selected.indexOf(option.properties.value) !== -1) { isSelected = true; }
        }
      }
    } else {
      if (this.properties.optionsSource === 'fromModel') {
        if (this.properties.selected === option[this.properties.optionValueField]) { isSelected = true; }
      } else {
        if (this.properties.selected === option.properties.value) { isSelected = true; }
      }
    }
    return isSelected ? 'mat-selected' : '';
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  selector: 'cf-select',
  template: "<div *ngIf=\"_inited && properties?.display\" class=\"{{properties?.iconPosition === 'after' ? 'icon-after' : 'icon-before'}} {{selectIsFocused ? 'cf-select-is-focused' : ''}} {{properties?.staticPlaceholder !== '' ? 'static-placeholder' : ''}} {{activeTemplate.style?.container?.class}} {{styling?.container?.class}} {{getDynamicClasses(activeTemplate.style?.container?.dynamicClass)}} {{getDynamicClasses(styling?.container?.dynamicClass)}}\" [ngClass]=\"'cf-select-container'\"> <cf-icon *ngIf=\"properties?.showIcon && properties?.iconProperty.name !== ''\" (click)=\"selectTrigger.open()\" [disable]=\"properties.disable\" [properties]=\"properties?.iconProperty\" [styling]=\"styling?.icon\" [ngClass]=\"properties.disable ? 'cf-icon-is-disabled' : ''\"></cf-icon> <span *ngIf=\"properties?.staticPlaceholder !== ''\" class=\"{{properties.disable ? 'disable' : ''}} {{activeTemplate.style?.staticPlaceholder?.class}} {{styling?.staticPlaceholder?.class}} {{getDynamicClasses(activeTemplate.style?.staticPlaceholder?.dynamicClass)}} {{getDynamicClasses(styling?.staticPlaceholder?.dynamicClass)}}\" [ngClass]=\"'cf-select-static-placeholder'\" (click)=\"selectTrigger.open()\">{{properties?.staticPlaceholder}}</span> <md-select #selectTrigger class=\"{{activeTemplate.style?.select?.class}} {{styling?.select?.class}} {{getDynamicClasses(activeTemplate.style?.select?.dynamicClass)}} {{getDynamicClasses(styling?.select?.dynamicClass)}}\" [ngClass]=\"'cf-select'\" [placeholder]=\"properties?.placeholder\" [(ngModel)]=\"properties.selected\" [required]=\"properties?.required\" disabled=\"{{properties.disable}}\" panelClass=\"{{properties?.dropdownUnder ? 'cf-dropdown-under' : ''}} {{id}} {{properties?.multiple ? 'multiple' : ''}} {{activeTemplate.style?.dropdownPanel?.class}} {{styling?.dropdownPanel?.class}} {{getDynamicClasses(activeTemplate.style?.dropdownPanel?.dynamicClass)}} {{getDynamicClasses(styling?.dropdownPanel?.dynamicClass)}}\" [multiple]=\"properties?.multiple\" (onOpen)=\"selectOpened()\" (change)=\"selectChanged()\" (onClose)=\"selectClosed()\" (focus)=\"setFocused(true)\" (blur)=\"setFocused(false)\"> <div *ngIf=\"properties?.showFilter\" class=\"cf-select-filter-container\"> <md-input-container class=\"{{activeTemplate.style?.filter?.class}} {{styling?.filter?.class}} {{getDynamicClasses(activeTemplate.style?.filter?.dynamicClass)}} {{getDynamicClasses(styling?.filter?.dynamicClass)}}\" [ngClass]=\"'cf-select-filter'\"> <input mdInput [(ngModel)]=\"cfFilterBy\" placeholder=\"Filter by:\" (ngModelChange)=\"filterOptions($event)\"> </md-input-container> </div> <div *ngIf=\"properties?.optionsSource === 'fromModel' && !!properties?.options\"> <div class=\"cf-option-wrapper from-model {{properties?.multiple ? 'cf-select-multiple-option' : ''}}\" *ngFor=\"let option of filterOptions(cfFilterBy)\" [hidden]=\"option?.display === false\"> <md-option class=\"cf-select-option\" [value]=\"option[properties.optionValueField]\" disabled=\"{{option?.disable === true}}\"> <span class=\"cf-select-option-text-hidden\">{{option[properties?.optionLabelField]}}</span> </md-option> <div class=\"mat-option {{option?.disable === true ? 'mat-option-disabled' : ''}} {{checkIfSelected(option)}}\" class=\"{{activeTemplate.style?.options?.class}} {{styling?.options?.class}} {{getDynamicClasses(activeTemplate.style?.options?.dynamicClass)}} {{getDynamicClasses(styling?.options?.dynamicClass)}}\" [ngClass]=\"'cf-option-data'\"> <cf-icon *ngIf=\"option[properties?.optionIconField]\" [properties]=\"option[properties?.optionIconField]\" [styling]=\"option?.styling?.icon\"></cf-icon> <span *ngIf=\"option[properties?.optionLabelField]\" class=\"{{option?.activeTemplate?.style?.label?.class}} {{option?.styling?.label?.class}} {{getDynamicClasses(option?.activeTemplate?.style?.label?.dynamicClass)}} {{getDynamicClasses(option?.styling?.label?.dynamicClass)}}\" [ngClass]=\"'cf-option-label'\">{{option[properties?.optionLabelField]}}</span> <span *ngIf=\"option[properties?.optionItemField]\" class=\"{{option?.activeTemplate?.style?.label?.class}} {{option?.styling?.label?.class}} {{getDynamicClasses(option?.activeTemplate?.style?.label?.dynamicClass)}} {{getDynamicClasses(option?.styling?.label?.dynamicClass)}}\" [ngClass]=\"'cf-option-item'\">{{option[properties?.optionItemField]}}</span> </div> </div> </div> <div *ngIf=\"properties?.optionsSource !== 'fromModel' && !!contentOptions\"> <div class=\"cf-option-wrapper from-content {{properties?.multiple ? 'cf-select-multiple-option' : ''}}\" *ngFor=\"let option of filterOptions(cfFilterBy)\" [hidden]=\"option?.display === false\"> <md-option class=\"cf-select-option\" [value]=\"option.properties.value\" disabled=\"{{option?.disable === true}}\"> <span class=\"cf-select-option-text-hidden\">{{option.properties.label}}</span> </md-option> <div class=\"mat-option {{option?.disable === true ? 'mat-option-disabled' : ''}} {{checkIfSelected(option)}}\" class=\"{{activeTemplate.style?.options?.class}} {{styling?.options?.class}} {{getDynamicClasses(activeTemplate.style?.options?.dynamicClass)}} {{getDynamicClasses(styling?.options?.dynamicClass)}}\" [ngClass]=\"'cf-option-data'\"> <cf-icon *ngIf=\"option?.properties?.icon\" [properties]=\"option?.properties?.icon\" [styling]=\"option?.styling?.icon\"></cf-icon> <span *ngIf=\"!option._hasTemplate\" class=\"{{option?.activeTemplate?.style?.label?.class}} {{option?.styling?.label?.class}} {{getDynamicClasses(option?.activeTemplate?.style?.label?.dynamicClass)}} {{getDynamicClasses(option?.styling?.label?.dynamicClass)}}\" [ngClass]=\"'cf-option-label'\">{{option?.properties?.label}}</span> <span *ngIf=\"!option._hasTemplate\" class=\"{{option?.activeTemplate?.style?.label?.class}} {{option?.styling?.label?.class}} {{getDynamicClasses(option?.activeTemplate?.style?.label?.dynamicClass)}} {{getDynamicClasses(option?.styling?.label?.dynamicClass)}}\" [ngClass]=\"'cf-option-item'\">{{option?.properties?.item}}</span> <span #optionsTmpls class=\"{{option?.activeTemplate?.style?.label?.class}} {{option?.styling?.label?.class}} {{getDynamicClasses(option?.activeTemplate?.style?.label?.dynamicClass)}} {{getDynamicClasses(option?.styling?.label?.dynamicClass)}}\" [ngClass]=\"'cf-option-html-content'\"><ng-container [ngTemplateOutlet]=\"option.template\"></ng-container></span> </div> </div> </div> </md-select> <cf-notification *ngIf=\"notification && (notification?.value || notification?.icon)\" [settings]=\"notification\" (click)=\"cfNotificationAction(notification)\"></cf-notification> </div> ",
  styles: ["::ng-deep .mat-option.cf-select-option { position: absolute; z-index: 1; top: 0px; right: 0px; bottom: 0px; left: 0px; height: 100%; min-height: 100%; } ::ng-deep .cf-select-option-text-hidden { visibility: hidden !important; display: none !important; opacity: 0 !important; position: absolute !important; left: -100000px !important; } ::ng-deep .cf-option-wrapper { position: relative; z-index: 0; } ::ng-deep .cf-option-wrapper md-option { display: flex !important; align-items: center; } ::ng-deep .cf-option-wrapper.cf-select-multiple-option .cf-option-data { padding-left: 48px !important; } ::ng-deep .cf-option-data { display: flex; align-items: center; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; height: auto; min-height: 32px; padding: 8px 16px; font-size: 16px; line-height: 32px; text-align: left; text-decoration: none; cursor: pointer; outline: none; } ::ng-deep .cf-option-data cf-icon { margin-right: 4px; } ::ng-deep .cf-option-data > span { display: flex; align-items: center; } ::ng-deep .mat-select-panel.cf-dropdown-under { padding: 3px 8px 12px; margin-left: -8px; box-shadow: none; background: none; overflow: visible; max-height: 280px; } ::ng-deep .cf-dropdown-under .mat-select-content { max-width: 280px; overflow-x: auto; max-height: 256px; box-shadow: 0 5px 5px -3px rgba(0, 0, 0, 0.2), 0 8px 10px 1px rgba(0, 0, 0, 0.14), 0 3px 14px 2px rgba(0, 0, 0, 0.12); } ::ng-deep .cf-dropdown-under.multiple .mat-select-content { max-width: 326px; } ::ng-deep .mat-selected * { color: inherit; } :host { position: relative; z-index: 0; display: inline-table; } :host.notification-clickable cf-notification:hover { cursor: pointer; } :host ::ng-deep md-select { width: 100%; } :host ::ng-deep > div:not(.cf-select-is-focused) md-icon.mat-icon { color: rgba(0, 0, 0, 0.38); } :host ::ng-deep .static-placeholder > cf-icon, :host ::ng-deep .static-placeholder > .cf-select-static-placeholder { z-index: 10; } :host ::ng-deep .static-placeholder md-select { position: absolute; width: 100%; top: 0px; left: 0px; right: 0px; bottom: 0px; } :host ::ng-deep .static-placeholder md-select .mat-select-trigger { min-width: 0px; } :host ::ng-deep .static-placeholder md-select .mat-select-trigger .mat-select-placeholder, :host ::ng-deep .static-placeholder md-select .mat-select-trigger .mat-select-value { position: absolute !important; left: -10000px !important; display: none !important; opacity: 0 !important; } :host ::ng-deep .static-placeholder md-select .mat-select-trigger .mat-select-arrow { position: absolute; right: 0; } :host [hidden] { display: none !important; } .cf-select-container { display: flex; align-items: center; min-height: 30px; } .cf-select-container.icon-before cf-icon { margin-right: 4px; text-align: center; } .cf-select-container.icon-after { flex-direction: row-reverse; } .cf-select-container.icon-after cf-icon { margin-left: 4px; text-align: center; } .cf-select-filter-container { padding: 8px 16px; height: 40px; overflow: hidden; } .cf-select-filter-container md-input-container { width: 100%; } .cf-select-static-placeholder { margin-right: 20px; } .cf-select-static-placeholder:not(.disable) { cursor: pointer; } .cf-select-static-placeholder.disable { color: rgba(0, 0, 0, 0.38); } "],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CfSelectComponent),
      multi: true
    }
  ]
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: Renderer, },
{type: ElementRef, },
{type: TemplateService, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'notificationClickable': [{ type: HostBinding, args: ['class', ] },],
'contentOptions': [{ type: ContentChildren, args: [CfSelectOptionComponent, ] },],
'properties': [{ type: Input },],
'styling': [{ type: Input },],
'placeholder': [{ type: Input, args: ['placeholder', ] },],
'staticPlaceholder': [{ type: Input, args: ['staticPlaceholder', ] },],
'optionsSource': [{ type: Input, args: ['optionsSource', ] },],
'options': [{ type: Input, args: ['options', ] },],
'selected': [{ type: Input, args: ['selected', ] },],
'showFilter': [{ type: Input, args: ['showFilter', ] },],
'showIcon': [{ type: Input, args: ['showIcon', ] },],
'iconProperty': [{ type: Input, args: ['iconProperty', ] },],
'iconChangeable': [{ type: Input, args: ['iconChangeable', ] },],
'required': [{ type: Input, args: ['required', ] },],
'multiple': [{ type: Input, args: ['multiple', ] },],
'dropdownUnder': [{ type: Input, args: ['dropdownUnder', ] },],
'optionValueField': [{ type: Input, args: ['optionValueField', ] },],
'optionLabelField': [{ type: Input, args: ['optionLabelField', ] },],
'optionItemField': [{ type: Input, args: ['optionItemField', ] },],
'optionIconField': [{ type: Input, args: ['optionIconField', ] },],
'onOpen': [{ type: Output },],
'onChange': [{ type: Output },],
'onClose': [{ type: Output },],
'selectTrigger': [{ type: ViewChild, args: ['selectTrigger', ] },],
'optionsTmpls': [{ type: ViewChildren, args: ['optionsTmpls', ] },],
};
}

function CfSelectComponent_tsickle_Closure_declarations() {
/** @type {?} */
CfSelectComponent.decorators;
/**
 * @nocollapse
 * @type {?}
 */
CfSelectComponent.ctorParameters;
/** @type {?} */
CfSelectComponent.propDecorators;
/**
 * @hidden 
 * Bind class to component :host itself
 * @type {?}
 */
CfSelectComponent.prototype.notificationClickable;
/**
 * @hidden 
 * List of children options from html view
 * @type {?}
 */
CfSelectComponent.prototype.contentOptions;
/**
 * <b>SelectModel</b>
 * <p>Example of <b>SelectModel</b> Class:</p>
 * <pre>{{'{'}}
 *  placeholder: 'Select fruit: ',
 *  staticPlaceholder: 'Open list',
 *  optionsSource: 'fromModel',
 *  options: [
 *      {{'{'}} val: 'apl', text: 'Apple' {{'}'}},
 *      {{'{'}} val: 'ban', text: 'Banana' {{'}'}},
 *      {{'{'}} val: 'che', text: 'Cherry' {{'}'}}
 * ],
 *  selected: 'che',
 *  showFilter: false,
 *  showIcon: false,
 *  iconProperty: this.myFruitsIcon,
 *  iconChangeable: false,
 *  required: false,
 *  multiple: true,
 *  dropdownUnder: true,
 *  optionValueField: 'val',
 *  optionLabelField: 'text',
 *  optionItemField: null,
 *  optionIconField: null
 * {{'}'}}</pre>
 * <h4>placeholder: string</h4>
 * <p>Placeholder text</p>
 * <h4>staticPlaceholder: string</h4>
 * <p>Not changable static placeholder text</p>
 * <h4>optionsSource: string</h4>
 * <p>Options source type</p>
 * <h4>options: any[]</h4>
 * <p>Array with options</p>
 * <h4>selected: any</h4>
 * <p>Select component must have some selected item and it is value of item property described in <b>itemValue</b></p>
 * <h4>showFilter: boolean</h4>
 * <p>Show/Hide filter</p>
 * <h4>showIcon: boolean</h4>
 * <p>Show/Hide icon</p>
 * <h4>iconProperty: IconModel</h4>
 * <p>Icon</p>
 * <h4>iconChangeable: boolean</h4>
 * <p>If main icon can be changed with selected option icon</p>
 * <h4>required: boolean</h4>
 * <p>Required</p>
 * <h4>multiple: boolean</h4>
 * <p>Options multipleselection</p>
 * <h4>dropdownUnder: boolean</h4>
 * <p>If select dropdown must be under it placeholder</p>
 * <h4>optionValueField: string</h4>
 * <p>Option property name to be used as option value</p>
 * <h4>optionLabelField: string</h4>
 * <p>Option property name to be used as select text when option is selected and as option text if item property is not specified</p>
 * <h4>optionItemField: string</h4>
 * <p>Option property name to be used as option item</p>
 * <h4>optionIconField: string</h4>
 * <p>Option property name to be used as option icon</p>
 * @type {?}
 */
CfSelectComponent.prototype.properties;
/**
 * <pre>{{'{'}}
 * // styling of the container surrounding the select
 * container: {{'{'}}
 * 		dynamic class: function() -> string, //function that returns a string of the name of the class
 * 		class: string //name of the class specified in you scss/css file
 * 	{{'}'}},
 * // Styling of select trigger by StylingModel
 * select: {{'{'}}
 * 		dynamic class: function() -> string,
 * 		class: string ,
 * 		themeColor: string , // primary/accent/warn
 * {{'}'}},
 * // Styling of filter by StylingModel
 * filter: {{'{'}}
 * 		dynamic class: function() -> string,
 * 		class: string
 * {{'}'}},
 * // Styling of options by StylingModel
 * options: {{'{'}}
 *    dynamic class: function() -> string,
 *    class: string
 * {{'}'}},
 * // Styling of static placeholder by StylingModel
 * staticPlaceholder: {{'{'}}
 *    dynamic class: function() -> string,
 *    class: string
 * {{'}'}},
 * // Styling of dropdown panel by StylingModel
 * dropdownPanel: {{'{'}}
 * 		dynamic class: function() -> string,
 * 		class: string
 * {{'}'}}
 * {{'}'}}
 * </pre>
 * @type {?}
 */
CfSelectComponent.prototype.styling;
/**
 * <p>Event to call when Select dropdown list was opened</p>
 * @type {?}
 */
CfSelectComponent.prototype.onOpen;
/**
 * <p>Event to call when value of Select was changed</p>
 * @type {?}
 */
CfSelectComponent.prototype.onChange;
/**
 * <p>Event to call when Select dropdown list was closed</p>
 * @type {?}
 */
CfSelectComponent.prototype.onClose;
/**
 * @hidden
 * @type {?}
 */
CfSelectComponent.prototype.selectTrigger;
/**
 * @hidden
 * @type {?}
 */
CfSelectComponent.prototype.optionsTmpls;
/**
 * @hidden 
 * <p>Value of the Filter input by which whole dropdown list items will be filtered<p>
 * @type {?}
 */
CfSelectComponent.prototype.cfFilterBy;
/**
 * @hidden 
 * True or false whether md-select is focused
 * @type {?}
 */
CfSelectComponent.prototype.selectIsFocused;
/**
 * @hidden
 * @type {?}
 */
CfSelectComponent.prototype._lastOffsetLeft;
/**
 * @hidden
 * @type {?}
 */
CfSelectComponent.prototype.onValueChange;
/**
 * @hidden
 * @type {?}
 */
CfSelectComponent.prototype.onTouched;
/** @type {?} */
CfSelectComponent.prototype._renderer;
/**
 * @hidden
 * @type {?}
 */
CfSelectComponent.prototype.elementRef;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
